/**
 * TabContextMenu - the roster bar's right-click menu, and the backdrop that closes it.
 *
 * The bar is a ROSTER bar (v7.2.0): a left-click sets your target; "Act as
 * this combatant" sets who you are - the same record. Tracker rows appear
 * only while the tracker runs and is not locked; tracking somebody is a
 * deliberate act, so it takes its own undo step, and the "someone joining
 * mid-round acts..." ruling decides the rest (v8.72.0). NO `tabs.length > 1`
 * guard on Close: an empty roster has been a real state since v8.29.0, and
 * the long way round (recall, then dismiss) was the only one that worked.
 *
 * Renders nothing while no menu is open. Extracted from DeathWatchSimulator.
 */
import { activatable } from '../../utils/keyActivation';
import common from '../../styles/common.module.css';

/** One menu entry: a styled item that answers a click, Enter and Space alike. */
function Item({ action, style, testId, children }) {
  return (
    <div className={common.dropdownItem} style={style} data-testid={testId} {...activatable(action)}>
      {children}
    </div>
  );
}

/** The tracker's row: add or remove, when the tracker is running and unlocked. */
function TrackerItem({ id, turnTracker, isLocked, close }) {
  if (!turnTracker.trackerState.active || isLocked('turnTracker')) return null;
  if (turnTracker.isTabTracked(id)) {
    const remove = () => {
      turnTracker.removeParticipant('tab-' + id);
      close();
    };
    return <Item action={remove}>Remove from Tracker</Item>;
  }
  const track = () => {
    close();
    turnTracker.addTabParticipant(id, { undoable: true });
  };
  return <Item action={track}>Track Participant</Item>;
}

/**
 * The GM's hide and reveal (plan map-display, G1): a hidden figure is kept off
 * every player's machine until revealed. Offered only when `hideReveal` is
 * given - the shell gives it to the GM alone.
 */
function HiddenItem({ id, hideReveal, hidden, close }) {
  if (!hideReveal) return null;
  if (hidden) {
    return (
      <Item testId="tab-reveal" action={() => (close(), hideReveal.reveal(id))}>
        Reveal to players
      </Item>
    );
  }
  return (
    <Item testId="tab-hide" action={() => (close(), hideReveal.hide(id))}>
      Hide from players
    </Item>
  );
}

const CONTACT_LEVELS = [
  { level: 'unknown', label: 'Contact: unknown', hint: 'Nothing on any screen' },
  { level: 'signs', label: 'Contact: signs', hint: "The tracker's one vague ring" },
  { level: 'contact', label: 'Contact: on the tracker', hint: 'A dot on its bearing, and a ? where it stands now' },
];

/**
 * What the party knows of a hidden figure (plan map-display, H; engine/stealth/contacts): the GM's three
 * levels, the one in force ticked. Setting Contact again marks it seen where it stands now.
 */
function ContactItems({ id, contacts, close }) {
  if (!contacts) return null;
  const now = contacts.levelOf(id);
  return CONTACT_LEVELS.map(({ level, label, hint }) => (
    <Item key={level} testId={`tab-contact-${level}`} action={() => (close(), contacts.setLevel(id, level))}>
      <span title={hint}>
        {now === level ? '✓ ' : ''}
        {label}
      </span>
    </Item>
  ));
}

/**
 * The GM's cut (plan map-display, C): every display camera to this figure,
 * for one move. Never offered for a hidden figure; a refusal comes back as a
 * sentence and is shown.
 */
function CutItem({ id, cutTo, notify, close }) {
  if (!cutTo) return null;
  const cut = () => {
    close();
    const refused = cutTo(id);
    if (refused) notify?.(refused);
  };
  return (
    <Item testId="tab-cut-to" action={cut}>
      Cut every camera here
    </Item>
  );
}

/** The GM's horde tools (plan map-display, B), on a horde only: split it in two, or break it - it flees. */
function HordeItems({ id, hordeTools, crowd, close }) {
  if (!hordeTools || !crowd) return null;
  return (
    <>
      <Item testId="tab-split-horde" action={() => (close(), hordeTools.split(id))}>
        Split horde in two
      </Item>
      <Item testId="tab-break-horde" action={() => (close(), hordeTools.break(id))}>
        Break horde (it flees)
      </Item>
    </>
  );
}

/**
 * @param {object} props
 * @param {{id: string, x: number, y: number}|null} props.tabCtx - where the menu opened, and on whom
 * @param {Function} props.setTabCtx
 * @param {Function} props.setRenamingTab
 * @param {string|null} props.actorId
 * @param {Function} props.setActorId
 * @param {Function} props.onDuplicate - (tabId, clone)
 * @param {Function} props.onClose - closes the TAB (removes the combatant), not the menu
 * @param {object} props.turnTracker
 * @param {Function} props.isLocked
 * @param {{hide: Function, reveal: Function}|null} [props.hideReveal] - the GM's verbs; absent for a player
 * @param {Function} [props.isHidden] - (id) => is this combatant hidden from the players
 * @param {{levelOf: Function, setLevel: Function}|null} [props.contacts] - a hidden figure's contact level (GM)
 */
export default function TabContextMenu(props) {
  const { tabCtx, setTabCtx, setRenamingTab, actorId, setActorId, onDuplicate, onClose } = props;
  if (!tabCtx) return null;
  const { id } = tabCtx;
  const hidden = !!props.isHidden?.(id);
  // An Object is only ever targeted (2026-10-26): no Act as, no seat in the order.
  const thing = !!props.isObjectAt?.(id);
  const close = () => setTabCtx(null);
  const then = (fn) => () => {
    fn();
    close();
  };
  return (
    <>
      {/* A click-away backdrop, not a control: Escape closes the menu too (useKeyboardShortcuts). */}
      <div role="presentation" style={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={close} />
      <div className={common.dropdown} style={{ position: 'fixed', left: tabCtx.x, top: tabCtx.y, zIndex: 1000 }}>
        <Item action={then(() => setRenamingTab(id))}>Rename</Item>
        {id !== actorId && !thing && <Item action={then(() => setActorId(id))}>Act as this combatant</Item>}
        {/* Never for a hidden figure (plan map-display, G1): a copy would be public, with its sheet and name,
            and it has no seat until revealed - the order's door would refuse it. */}
        {!hidden && (
          <>
            <Item action={() => onDuplicate(id, false)}>Duplicate (Fresh)</Item>
            <Item action={() => onDuplicate(id, true)}>Duplicate (Clone)</Item>
            {!thing && <TrackerItem id={id} turnTracker={props.turnTracker} isLocked={props.isLocked} close={close} />}
          </>
        )}
        <HiddenItem id={id} hideReveal={props.hideReveal} hidden={hidden} close={close} />
        {hidden && <ContactItems id={id} contacts={props.contacts} close={close} />}
        {!hidden && <HordeItems id={id} hordeTools={props.hordeTools} crowd={!!props.isCrowdAt?.(id)} close={close} />}
        {!hidden && <CutItem id={id} cutTo={props.cutTo} notify={props.notify} close={close} />}
        <Item testId="tab-close" action={() => onClose(id)} style={{ color: 'var(--danger)' }}>
          Close
        </Item>
      </div>
    </>
  );
}
